<script setup lang="ts">
import { computed, onMounted, ref, onUnmounted, watch } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import { useTabStore } from '@/stores/tabs.store'
import { IDEVIEWTABS, TABS_WIDTH, TABS_WIDTH_TYPE } from '@/utils/tabs'
import tabsService from '@/services/ide/tabs.service'
import OutputHeader from '@/components/ide/ide/OutputHeader.vue'
import OutputComp from '@/components/ide/ide/OutputComp.vue'
import GeneratedFileHeader from '@/components/ide/ide/GeneratedFileHeader.vue'
import GenaratedFileComp from '@/components/ide/ide/GenaratedFileComp.vue'
import InputFileComp from '@/components/ide/ide/InputFileComp.vue'
import LibraryComp from '@/components/ide/ide/LibraryComp.vue'
import InputComp from '@/components/ide/ide/InputComp.vue'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import Jdroid from '@/components/ide/ide/ai/jdroid/JDroidComp.vue'

import TabsComponent from '@/components/ide/ide/OutputAndGeneratedFilesTabComp.vue'
import BrowserComp from '@/components/ide/ide/BrowserComp.vue'
import DbTerminal from '@/components/ide/ide/DatabaseTerminal.vue'
import ApiTabComp from '@/components/ide/ide/tabs/ApiTabComp/ApiTabComp.vue'
import TerminalComp from '@/components/ide/ide/TerminalComp.vue'
import ConsoleComp from '@/components/ide/ide/ConsoleComp.vue'

const ideStore = useIdeStore()
const tabStore = useTabStore()

const tabSizeRef = ref<HTMLElement | null>(null)

const tabNameCurrentlyVisible = computed(() => {
  return tabsService.checkTabNameCurrentlyVisibleOnBottom() || null
})

const showIORef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.IO, true)
})
const showExtLibRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.EXTERNAL_LIBRARIES, true)
})
const showUploadResRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.UPLOAD_FILES, true)
})
const showJroidRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.JDROID, true)
})

const showBrowserRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.BROWSER, true)
})

const showDbTerminalRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.DB_TERMINAL, true)
})

const showApiTabRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.API_TAB, true)
})

const showTerminalRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.TERMINAL, true)
})

const showConsoleRef = computed(() => {
  return tabsService.checkBottomTab(TAB_ICONS_NAMES.CONSOLE, true)
})

const isShowLibs = computed(() => {
  return ideStore.ideMeta?.isPlus && ideStore.ideMeta?.library
})
const currentMobileTab = computed(() => {
  return ideStore.currentMobileTab
})

const resizeObserver = new ResizeObserver((entries) => {
  updateTabWidth(entries[0]?.borderBoxSize?.[0]?.inlineSize)
})
/**
 * Updates the current tab's width type based on the provided inner width.
 * @param innerWidth - The width of the tab content in pixels.
 */
const updateTabWidth = (innerWidth: number) => {
  if (innerWidth) {
    if (innerWidth < TABS_WIDTH.LARGE && innerWidth < TABS_WIDTH.MEDIUM) {
      tabStore.setTabWidthCurrentSizeBottom(TABS_WIDTH_TYPE.SMALL, tabNameCurrentlyVisible.value)
    } else if (innerWidth < TABS_WIDTH.LARGE && innerWidth > TABS_WIDTH.MEDIUM) {
      tabStore.setTabWidthCurrentSizeBottom(TABS_WIDTH_TYPE.MEDIUM, tabNameCurrentlyVisible.value)
    } else {
      tabStore.setTabWidthCurrentSizeBottom(TABS_WIDTH_TYPE.LARGE, tabNameCurrentlyVisible.value)
    }
  }
}

onMounted(() => {
  if (tabSizeRef.value !== null) {
    resizeObserver.observe(tabSizeRef.value)
    updateTabWidth(tabSizeRef.value.getBoundingClientRect().width)
  }
})

onUnmounted(() => {
  if (tabSizeRef.value !== null) {
    resizeObserver.unobserve(tabSizeRef.value)
  }
})

watch(
  () => tabNameCurrentlyVisible.value,
  () => {
    if (tabSizeRef.value) {
      updateTabWidth(tabSizeRef.value.getBoundingClientRect().width)
    }
  }
)
</script>

<template>
  <div ref="tabSizeRef" class="h-auto w-full">
    <div class="h-full">
      <div class="h-full w-full">
        <Jdroid
          class="active flex h-full items-stretch"
          v-if="showJroidRef"
          :currentPosition="TAB_BUTTON_POSITION.BOTTOM"
          role="tabpanel"
          :class="showJroidRef ? 'visible' : 'hidden'"
          :id="`${IDEVIEWTABS.JDROID}`"
          :aria-labelledby="`${IDEVIEWTABS.JDROID}-item`"
          :tabPosition="TAB_BUTTON_POSITION.BOTTOM"
        />

        <div
          v-if="isShowLibs && showExtLibRef"
          :id="`${IDEVIEWTABS.LIBRARIES}`"
          class="h-full"
          :class="showExtLibRef ? 'visible' : 'hidden'"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.LIBRARIES}-item`"
        >
          <div class="h-full w-full">
            <LibraryComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
          </div>
        </div>

        <div
          v-if="showUploadResRef"
          :id="`${IDEVIEWTABS.INPUTFILES}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.INPUTFILES}-item`"
        >
          <div class="h-full w-full">
            <InputFileComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
          </div>
        </div>
        <div
          v-if="showIORef"
          class="relative flex h-full w-full sm:flex-col lg:flex-col"
          :id="`${IDEVIEWTABS.IO}`"
        >
          <div
            class="surface-secondary-base flex h-full w-full flex-col overflow-auto sm:pb-4"
            :id="`${IDEVIEWTABS.IO}-main`"
          >
            <div
              class="h-fit w-full md:pr-0 lg:min-w-0"
              role="tabpanel"
              :aria-labelledby="`${IDEVIEWTABS.INPUT}-item`"
            >
              <div class="h-full w-full">
                <InputComp :currentTabPosition="TAB_BUTTON_POSITION.BOTTOM" />
              </div>
            </div>
            <div
              class="h-full w-full"
              role="tabpanel"
              :aria-labelledby="`${IDEVIEWTABS.OUTPUT}-item`"
            >
              <div class="h-full w-full md:ml-0">
                <TabsComponent :tabs="[{ label: OutputHeader }, { label: GeneratedFileHeader }]">
                  <template #slot1>
                    <OutputComp v-if="currentMobileTab === null" />
                  </template>
                  <template #slot2>
                    <GenaratedFileComp />
                  </template>
                </TabsComponent>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showBrowserRef"
          :id="`${IDEVIEWTABS.BROWSER}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.BROWSER}-item`"
        >
          <BrowserComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
        </div>
        <div
          v-if="showApiTabRef"
          :id="`${IDEVIEWTABS.API_TAB}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.API_TAB}-item`"
        >
          <ApiTabComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
        </div>

        <div
          v-if="showDbTerminalRef"
          :id="`${IDEVIEWTABS.DB_TERMINAL}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.DB_TERMINAL}-item`"
        >
          <DbTerminal :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
        </div>
        <div
          v-if="showTerminalRef"
          :id="`${IDEVIEWTABS.TERMINAL}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.TERMINAL}-item`"
        >
          <TerminalComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
        </div>
        <div
          v-if="showConsoleRef"
          :id="`${IDEVIEWTABS.CONSOLE}`"
          class="h-full"
          role="tabpanel"
          :aria-labelledby="`${IDEVIEWTABS.CONSOLE}-item`"
        >
          <ConsoleComp :tabPosition="TAB_BUTTON_POSITION.BOTTOM" />
        </div>
      </div>
    </div>
  </div>
</template>
